import { pad } from '@/helpers.tsx';
import { _chunk } from '@/littledash';
import type { Animal as SystemAnimal } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model.ts';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';

// Types - exported for use in other files
export interface RecagingAnimal extends SystemAnimal {
  newAnimalId?: string;
}

export interface Subject extends SystemAnimal {
  newAnimalId?: string;
}

// Helper function to add newAnimalId to SystemAnimal
const addNewAnimalId = (subject: SystemAnimal): RecagingAnimal => ({
  ...subject,
  newAnimalId: '',
});

export interface GroupedAnimals {
  group: TreatmentGroup;
  animals: RecagingAnimal[];
}

export interface CollectionGroup {
  collection_id?: string;
  collection?: string;
  animals: RecagingAnimal[];
}

export interface ProposedCage {
  id: string;
  cage: string;
  treatmentGroup: TreatmentGroup;
  animals: RecagingAnimal[];
}

export const SEQUENTIAL = 'SEQUENTIAL';
export const REPEATING = 'REPEATING';
export const CAGE_NUMBER = 'CAGE_NUMBER';
export const GROUP_NUMBER = 'GROUP_NUMBER';
export const GROUP_NAME = 'GROUP_NAME';

export const ID_METHODS = [
  {
    value: SEQUENTIAL,
    label: 'Sequential',
  },
  {
    value: REPEATING,
    label: 'Repeating',
  },
];

export const CAGE_NAMING_METHODS = [
  {
    value: CAGE_NUMBER,
    label: 'Cage number',
  },
  {
    value: GROUP_NUMBER,
    label: 'Group number',
  },
  {
    value: GROUP_NAME,
    label: 'Group name',
  },
];

export const groupAnimalsByTreatmentGroup = (accumulator: GroupedAnimals[], subject: Subject): GroupedAnimals[] => {
  const foundIndex = accumulator.findIndex(({ group }) => group.id === subject.study_group?.id);

  if (foundIndex !== -1) {
    accumulator[foundIndex].animals.push(addNewAnimalId(subject));
  } else {
    if (subject.study_group) {
      accumulator.push({
        group: subject.study_group,
        animals: [addNewAnimalId(subject)],
      });
    }
  }

  return accumulator;
};

export const groupAnimalsWithoutGroups = (accumulator: GroupedAnimals[], subject: Subject): GroupedAnimals[] => {
  const groupName = 'Unassigned';
  const foundIndex = accumulator.findIndex(({ group }) => group.name === groupName);

  if (foundIndex !== -1) {
    accumulator[foundIndex].animals.push(addNewAnimalId(subject));
  } else {
    accumulator.push({
      group: {
        id: 0,
        name: groupName,
        no: 0,
        color: '#000000',
        max_subjects: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as TreatmentGroup,
      animals: [addNewAnimalId(subject)],
    });
  }

  return accumulator;
};

export const groupAnimalsByCollectionId = (
  accumulator: CollectionGroup[],
  subject: RecagingAnimal
): CollectionGroup[] => {
  const foundIndex = accumulator.findIndex(({ collection_id }) => collection_id === String(subject.cage?.id || ''));
  if (foundIndex !== -1) {
    accumulator[foundIndex].animals.push(subject);
    return accumulator;
  }
  accumulator.push({
    collection_id: String(subject.cage?.id || ''),
    collection: subject.cage?.name,
    animals: [subject],
  });
  return accumulator;
};

export const sortAnimalsByFrequency = (a: CollectionGroup, b: CollectionGroup): number =>
  b.animals.length - a.animals.length;

export const sortAnimalsByNumber = (a: RecagingAnimal, b: RecagingAnimal): number => (a.number || 0) - (b.number || 0);

export const spreadAnimals = (accumulator: RecagingAnimal[], group: CollectionGroup): RecagingAnimal[] => {
  accumulator.push(...group.animals);
  return accumulator;
};

export const chunkGroups = (groups: RecagingAnimal[], chunkSize: number): RecagingAnimal[][] =>
  _chunk(groups, chunkSize);

export const groupAndSortAnimals = (subjects: Subject[]): GroupedAnimals[] => {
  const withGroups = subjects
    .filter((subject: Subject) => subject.study_group && subject.study_group.id)
    .reduce(groupAnimalsByTreatmentGroup, [])
    .sort((a: GroupedAnimals, b: GroupedAnimals) => Number(a.group.no || 0) - Number(b.group.no || 0))
    .map((group: GroupedAnimals) => ({
      ...group,
      animals: group.animals
        .reduce(groupAnimalsByCollectionId, [])
        .sort(sortAnimalsByFrequency)
        .reduce(spreadAnimals, [])
        .sort(sortAnimalsByNumber),
    }));

  const withoutGroups = subjects
    .filter((subject: Subject) => !subject.study_group || !subject.study_group.id)
    .reduce(groupAnimalsWithoutGroups, [])
    .map((group: GroupedAnimals) => ({
      ...group,
      animals: group.animals
        .reduce(groupAnimalsByCollectionId, [])
        .sort(sortAnimalsByFrequency)
        .reduce(spreadAnimals, [])
        .sort(sortAnimalsByNumber),
    }));

  return [...withGroups, ...withoutGroups];
};

export const generateRepeatingIds = (proposedCages: ProposedCage[], ids: Array<string>): ProposedCage[] => {
  const cages = proposedCages ? [...proposedCages] : [];
  const cageCountRecord: { [key: ID]: number } = {};

  for (let cageIndex = 0; cageIndex < cages.length; cageIndex++) {
    const animals = cages[cageIndex].animals;
    for (let index = 0; index < animals?.length; index++) {
      const cageId = cages[cageIndex].id;

      if (!cageId) {
        continue;
      }

      if (!cageCountRecord[cageId]) {
        cageCountRecord[cageId] = 0;
      }

      if (ids[cageCountRecord[cageId]]) {
        cages[cageIndex].animals[index].newAnimalId = ids[cageCountRecord[cageId]];
      }
      cageCountRecord[cageId]++;
    }
  }
  return cages;
};

export const generateSequentialIds = (
  proposedCages: ProposedCage[],
  sequencePrefix: string,
  sequenceStarting: string
): ProposedCage[] => {
  const cages = proposedCages ? [...proposedCages] : [];
  let index = 0;

  return cages.map((cage) => {
    return {
      ...cage,
      animals: cage.animals
        ? cage.animals?.map((animal: RecagingAnimal) => ({
            ...animal,
            newAnimalId: `${sequencePrefix}${pad(Number(sequenceStarting) + index++, String(sequenceStarting).length)}`,
          }))
        : [],
    };
  });
};

export const getHighestCageGroupName = (groupNumber: string | number, studyCageNames: string[]): string | null => {
  const groupNameMatches = studyCageNames.filter(
    (name: string) =>
      name.match(new RegExp(`^${groupNumber}([A-Z])$`)) || name.match(new RegExp(`^${groupNumber}([A-Z][A-Z])$`))
  );

  if (groupNameMatches.length) {
    groupNameMatches.map((name: string) => name.replace(/^\d+/, ''));

    // Return the highest group name
    return groupNameMatches
      .sort((a: string, b: string) => {
        // Compare by length first (AA > Z)
        if (a.length !== b.length) {
          return a.length - b.length;
        }

        // If lengths are equal, compare alphabetically
        return a.localeCompare(b);
      })
      .reverse()[0]
      .replace(/^\d+/, '');
  }

  return null;
};

export const determineNextCageGroupName = (highestGroupName: string | null): string => {
  if (highestGroupName === null) {
    return 'A';
  }

  if (highestGroupName.length === 1) {
    if (highestGroupName === 'Z') {
      return 'AA';
    }
    return String.fromCharCode(highestGroupName.charCodeAt(0) + 1);
  }

  if (highestGroupName.length === 2) {
    if (highestGroupName[1] === 'Z') {
      return `${String.fromCharCode(highestGroupName.charCodeAt(0) + 1)}A`;
    }
    return `${highestGroupName[0]}${String.fromCharCode(highestGroupName.charCodeAt(1) + 1)}`;
  }

  return 'A'; // fallback
};
